import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, Chip, IconButton, CircularProgress } from '@mui/material';
import { AssignmentLate, NavigateBefore, NavigateNext, Close, ArrowForward } from '@mui/icons-material';
import styled from '@emotion/styled';
import PageHeader from '../components/PageHeader';
import { getUnassignedSummary, getUnassignedList } from '../redux/ducks/project';
import { formatTimeAgo } from '../utils/dateUtils';

// Quadrant metadata: department x skill presence. Keys match the API's cell filters.
const CELLS = {
    'dept-skill': { label: 'Routed — needs owner', hint: 'Has a department and a skill; just needs a person.', field: 'deptSkill' },
    'dept-noskill': { label: 'Dept set, no skill', hint: 'Routed to a department but no skill yet.', field: 'deptNoSkill' },
    'nodept-skill': { label: 'Skill only', hint: 'Has a skill but no department.', field: 'noDeptSkill' },
    'nodept-noskill': { label: 'Brand new — untouched', hint: 'No department and no skill — nobody has triaged it.', field: 'noDeptNoSkill' },
};

const StyledContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6),
}));

const Matrices = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
    },
}));

const Card = styled(Box, { shouldForwardProp: (prop) => prop !== '$focused' })(({ theme, $focused }) => ({
    padding: theme.spacing(2),
    border: `1px solid ${$focused ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
}));

const CardTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.lg,
    marginBottom: theme.spacing(1.5),
}));

const CardCount = styled('span')(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    marginLeft: theme.spacing(0.75),
}));

const Grid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr 1fr',
    gap: theme.spacing(1),
    alignItems: 'stretch',
}));

const AxisTop = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    textAlign: 'center',
    alignSelf: 'end',
    paddingBottom: theme.spacing(0.5),
}));

const AxisLeft = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    textAlign: 'center',
    justifySelf: 'center',
    padding: theme.spacing(0.5, 0),
}));

const Quadrant = styled(Box, { shouldForwardProp: (prop) => prop !== '$selected' && prop !== '$empty' })(({ theme, $selected, $empty }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.25),
    minHeight: 84,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${$selected ? theme.palette.primary.main : theme.palette.divider}`,
    backgroundColor: $selected ? theme.palette.action.selected : theme.palette.surface.inset,
    cursor: $empty ? 'default' : 'pointer',
    opacity: $empty ? 0.55 : 1,
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': $empty ? {} : {
        borderColor: theme.palette.primary.main,
    },
}));

const QuadrantCount = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xl,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1,
    color: theme.palette.text.primary,
}));

const QuadrantLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.smd,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
}));

const QuadrantHint = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
}));

const Panel = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
}));

const PanelHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const PanelTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
}));

const ItemRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
        backgroundColor: theme.palette.surface.inset,
    },
    '&:last-of-type': {
        borderBottom: 'none',
    },
}));

const ItemMain = styled(Box)({
    minWidth: 0,
    flex: 1,
});

const ItemName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const ItemMeta = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
}));

const ItemAccount = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
}));

const ItemTime = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    flexShrink: 0,
}));

const Pager = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 2),
}));

const PagerLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
}));

const CenterState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minHeight: 160,
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const MatrixCard = ({ type, bucket, selectedCell, onSelect, focused }) => {
    const cells = bucket?.cells || {};
    const renderQuadrant = (key) => {
        const cfg = CELLS[key];
        const value = cells[cfg.field] || 0;
        const empty = value === 0;
        return (
            <Quadrant
                $selected={selectedCell === key}
                $empty={empty}
                onClick={() => !empty && onSelect(type, key)}
                role={empty ? undefined : 'button'}
                tabIndex={empty ? undefined : 0}
            >
                <QuadrantCount>{value}</QuadrantCount>
                <QuadrantLabel>{cfg.label}</QuadrantLabel>
                <QuadrantHint>{cfg.hint}</QuadrantHint>
            </Quadrant>
        );
    };

    return (
        <Card $focused={focused}>
            <CardTitle>
                {type === 'project' ? 'Projects' : 'Tasks'}
                <CardCount>{bucket?.total || 0}</CardCount>
            </CardTitle>
            <Grid>
                <Box />
                <AxisTop>Has skill</AxisTop>
                <AxisTop>No skill</AxisTop>
                <AxisLeft>Has dept</AxisLeft>
                {renderQuadrant('dept-skill')}
                {renderQuadrant('dept-noskill')}
                <AxisLeft>No dept</AxisLeft>
                {renderQuadrant('nodept-skill')}
                {renderQuadrant('nodept-noskill')}
            </Grid>
        </Card>
    );
};

const UnassignedTriage = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const focusType = searchParams.get('type') === 'task' ? 'task' : searchParams.get('type') === 'project' ? 'project' : null;

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [selected, setSelected] = useState(null); // { type, cell }
    const [list, setList] = useState(null); // { items, total, start, pageSize }
    const [listLoading, setListLoading] = useState(false);

    useEffect(() => {
        setSummaryLoading(true);
        dispatch(getUnassignedSummary((data) => {
            setSummaryLoading(false);
            setSummary(data || null);
        }));
    }, [dispatch]);

    const loadList = useCallback((type, cell, start) => {
        setListLoading(true);
        dispatch(getUnassignedList(type, cell, start, (data) => {
            setListLoading(false);
            setList(data || null);
        }));
    }, [dispatch]);

    const handleSelect = (type, cell) => {
        if (selected && selected.type === type && selected.cell === cell) {
            setSelected(null);
            setList(null);
            return;
        }
        setSelected({ type, cell });
        setList(null);
        loadList(type, cell, 0);
    };

    const handlePage = (delta) => {
        if (!selected || !list) return;
        const pageSize = list.pageSize || 25;
        const nextStart = Math.max(0, (list.start || 0) + delta * pageSize);
        loadList(selected.type, selected.cell, nextStart);
    };

    const itemLink = (item, type) => {
        const projectId = type === 'task' ? (item.project?._id || item.project) : item._id;
        return `/account/${item.account?.slug}/project/${projectId}`;
    };

    const totalUnassigned = (summary?.projects?.total || 0) + (summary?.tasks?.total || 0);
    const pageSize = list?.pageSize || 25;
    const start = list?.start || 0;
    const total = list?.total || 0;

    return (
        <StyledContainer maxWidth="lg">
            <PageHeader
                title="Unassigned Work"
                subtitle="Projects and tasks with no one responsible. Pick a bucket to see what needs routing or an owner."
            />

            {summaryLoading ? (
                <CenterState>
                    <CircularProgress size={28} />
                </CenterState>
            ) : totalUnassigned === 0 ? (
                <CenterState>
                    <AssignmentLate sx={{ fontSize: 44, opacity: 0.5 }} />
                    <Typography variant="body1">Everything has an owner — nothing unassigned right now.</Typography>
                </CenterState>
            ) : (
                <>
                    <Matrices>
                        <MatrixCard
                            type="project"
                            bucket={summary.projects}
                            selectedCell={selected?.type === 'project' ? selected.cell : null}
                            onSelect={handleSelect}
                            focused={focusType === 'project'}
                        />
                        <MatrixCard
                            type="task"
                            bucket={summary.tasks}
                            selectedCell={selected?.type === 'task' ? selected.cell : null}
                            onSelect={handleSelect}
                            focused={focusType === 'task'}
                        />
                    </Matrices>

                    {selected && (
                        <Panel>
                            <PanelHeader>
                                <PanelTitle>
                                    {selected.type === 'project' ? 'Projects' : 'Tasks'} · {CELLS[selected.cell].label}
                                    {!listLoading && list ? ` — ${total}` : ''}
                                </PanelTitle>
                                <IconButton size="small" onClick={() => { setSelected(null); setList(null); }} aria-label="Close list">
                                    <Close fontSize="small" />
                                </IconButton>
                            </PanelHeader>

                            {listLoading ? (
                                <CenterState>
                                    <CircularProgress size={24} />
                                </CenterState>
                            ) : !list || list.items.length === 0 ? (
                                <CenterState>
                                    <Typography variant="body2">Nothing to show in this bucket.</Typography>
                                </CenterState>
                            ) : (
                                <>
                                    {list.items.map((item) => (
                                        <ItemRow key={item._id} component={Link} to={itemLink(item, selected.type)}>
                                            <ItemMain>
                                                <ItemName>{item.name}</ItemName>
                                                <ItemMeta>
                                                    {item.account?.name && <ItemAccount>{item.account.name}</ItemAccount>}
                                                    {item.department?.name && (
                                                        <Chip size="small" variant="outlined" label={item.department.name} />
                                                    )}
                                                    {item.skill?.name && (
                                                        <Chip size="small" variant="outlined" color="primary" label={item.skill.name} />
                                                    )}
                                                </ItemMeta>
                                            </ItemMain>
                                            <ItemTime>{formatTimeAgo(item.updatedAt)}</ItemTime>
                                        </ItemRow>
                                    ))}
                                    {total > pageSize && (
                                        <Pager>
                                            <PagerLabel>{start + 1}–{Math.min(start + pageSize, total)} of {total}</PagerLabel>
                                            <IconButton size="small" onClick={() => handlePage(-1)} disabled={start === 0} aria-label="Previous page">
                                                <NavigateBefore fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handlePage(1)} disabled={start + pageSize >= total} aria-label="Next page">
                                                <NavigateNext fontSize="small" />
                                            </IconButton>
                                        </Pager>
                                    )}
                                </>
                            )}
                        </Panel>
                    )}

                    {!selected && (
                        <Box mt={3} display="flex" alignItems="center" gap={1} color="text.secondary">
                            <ArrowForward fontSize="small" />
                            <Typography variant="body2">Select a bucket above to list the items in it.</Typography>
                        </Box>
                    )}
                </>
            )}
        </StyledContainer>
    );
};

export default UnassignedTriage;
